import { useEffect, useRef, useState } from 'react'
import { View } from 'react-native'
import Animated, {
  useAnimatedStyle,
  useSharedValue,
  withSpring,
  WithSpringConfig,
} from 'react-native-reanimated'
import { runOnJS } from 'react-native-worklets'
import cn from '@ui/utils/cn'

type SpringConfigNoTiming = Omit<
  WithSpringConfig,
  'duration' | 'dampingRatio' | 'clamp'
>

type Props = {
  children: React.ReactNode
  open: boolean
  config?: SpringConfigNoTiming
  fullWidth?: boolean
  defaultHeight?: number
  className?: string
  /**
   * Respect `open`, but render children in normal layout flow without creating
   * animation or measurement state.
   */
  animated?: boolean
  /**
   * Mount children only while open: deferred until the first open, and unmounted
   * again once the collapse animation finishes. Cheaper initial render, cheaper
   * per-frame relayout while a sibling animates, and reclaims memory when closed.
   * Only safe for display-only content — do NOT use for form fields that must
   * register/submit while collapsed.
   */
  lazy?: boolean
}

type AnimatedCollapsibleProps = Omit<Props, 'animated'>

const AnimatedCollapsible = ({
  children,
  open,
  config,
  fullWidth,
  defaultHeight,
  className,
  lazy,
}: AnimatedCollapsibleProps) => {
  const layoutHeight = useSharedValue(defaultHeight || 400)
  const animation = useSharedValue(open && !lazy ? 1 : 0)

  const [mounted, setMounted] = useState(!lazy || open)
  const [measured, setMeasured] = useState(!lazy)
  const shouldMount = !lazy || mounted || open

  // Rasterize the (static) content into a single cached bitmap while it slides,
  // so iOS composites instead of re-rasterizing text every frame. On at the
  // start of a toggle, off once the spring settles. No-op on web/Android.
  const [rasterize, setRasterize] = useState(false)
  const isFirst = useRef(true)

  // Read the latest `open` at settle time
  const openRef = useRef(open)
  useEffect(() => {
    openRef.current = open
  }, [open])

  useEffect(() => {
    if (isFirst.current) {
      isFirst.current = false
      return
    }

    if (!shouldMount) {
      return
    }

    // Lazy content is first mounted at height zero and measured. Only then do
    // we start opening, avoiding the fallback height changing mid-animation.
    if (lazy && open && !measured) {
      return
    }

    const target = open ? 1 : 0
    const handleSettled = (settledOpen: boolean) => {
      if (settledOpen !== openRef.current) {
        return
      }
      setRasterize(false)
      // Unmount once the collapse animation is fully done
      if (lazy && !settledOpen) {
        setMounted(false)
        setMeasured(false)
      }
    }

    const handleAnimationEnd = (finished?: boolean) => {
      'worklet'
      if (finished) {
        runOnJS(handleSettled)(open)
      }
    }

    const rasterizeFrame = !lazy
      ? requestAnimationFrame(() => {
          setRasterize(true)
        })
      : undefined

    animation.set(
      withSpring(
        target,
        {
          mass: 1,
          damping: 20,
          stiffness: 100,
          overshootClamping: true,
          ...config,
        },
        handleAnimationEnd
      )
    )

    return () => {
      if (rasterizeFrame !== undefined) {
        cancelAnimationFrame(rasterizeFrame)
      }
    }
  }, [animation, config, lazy, measured, open, shouldMount])

  const style = useAnimatedStyle(() => {
    return {
      opacity: animation.value,
      height: layoutHeight.value * animation.value,
    }
  })

  return (
    <Animated.View
      style={style}
      className={cn(
        'relative overflow-hidden',
        fullWidth && 'w-full',
        className
      )}
    >
      <Animated.View
        shouldRasterizeIOS={rasterize || (lazy && mounted && !open)}
        className={cn('absolute left-0 top-0', fullWidth && 'w-full')}
        onLayout={({ nativeEvent: { layout } }) => {
          layoutHeight.set(layout.height)
          if (lazy && open && !measured) {
            setMounted(true)
            setMeasured(true)
            setRasterize(true)
          }
        }}
      >
        {shouldMount ? children : null}
      </Animated.View>
    </Animated.View>
  )
}

export const Collapsible = ({ animated = true, ...props }: Props) => {
  if (!animated) {
    if (!props.open) {
      return null
    }

    return (
      <View className={cn(props.fullWidth && 'w-full', props.className)}>
        {props.children}
      </View>
    )
  }

  return <AnimatedCollapsible {...props} />
}
